import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon, UserIcon, EnvelopeIcon, PhoneIcon, BuildingOfficeIcon,
  CalendarIcon, ClockIcon, PencilIcon, ChatBubbleLeftRightIcon, UserGroupIcon,
  BanknotesIcon, TagIcon, CheckCircleIcon, ExclamationCircleIcon,
  ChevronDownIcon, ChevronUpIcon, XMarkIcon, ArrowPathIcon, TrashIcon
} from '@heroicons/react/24/outline';
import { leadsApi } from '../config/api';
import LeadsService from '../services/leadsService';
import LeadForm from './LeadForm';
import StatusChangeModal from './StatusChangeModal';
import FollowUpModal from './FollowUpModal';
import { useAuth } from '../context/AuthContext';

const LeadProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusModal, setStatusModal] = useState({ isOpen: false, viewHistoryOnly: false, newStatus: '' });
  const [leadStatuses, setLeadStatuses] = useState([]);
  const [expandedSections, setExpandedSections] = useState({ contact: true, status: true, followups: true });
  const [rescheduleModal, setRescheduleModal] = useState({ isOpen: false, followUp: null, followUpIndex: -1 });
  const [rescheduleData, setRescheduleData] = useState({ newDate: '', reason: '', reasonType: '', notes: '' });
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [expandedHistories, setExpandedHistories] = useState({}); // Track which follow-up histories are expanded
  const [scheduleFollowUpModal, setScheduleFollowUpModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  // Get current user - from context or localStorage (check multiple keys)
  const getCurrentUser = () => {
    if (user) return user;
    // Fallback to localStorage - check multiple possible keys
    try {
      // Try userInfo first (used by AuthContext)
      let userInfo = localStorage.getItem('userInfo');
      if (userInfo) {
        const parsed = JSON.parse(userInfo);
        console.log('📦 Found userInfo in localStorage:', parsed);
        return parsed;
      }
      
      // Try userData (used by some components)
      userInfo = localStorage.getItem('userData');
      if (userInfo) {
        const parsed = JSON.parse(userInfo);
        console.log('📦 Found userData in localStorage:', parsed);
        return parsed;
      }
      
      // Try to decode from token
      const token = localStorage.getItem('authToken');
      if (token) {
        try {
          // Decode JWT token (base64)
          const payload = JSON.parse(atob(token.split('.')[1]));
          console.log('📦 Decoded token payload:', payload);
          // Also check for stored role/name
          const userRole = localStorage.getItem('userRole');
          const userId = localStorage.getItem('userId');
          
          // Extract name from email if not available
          let userName = payload.name;
          if (!userName && payload.email) {
            // Get name part from email (before @)
            userName = payload.email.split('@')[0];
            // Capitalize first letter
            userName = userName.charAt(0).toUpperCase() + userName.slice(1);
          }
          
          return {
            id: payload.id || userId,
            email: payload.email,
            role: payload.role || userRole,
            userType: payload.userType || userRole,
            name: userName
          };
        } catch (e) {
          console.error('Error decoding token:', e);
        }
      }
    } catch (e) {
      console.error('Error getting user from localStorage:', e);
    }
    return null;
  };

  const currentUserObj = getCurrentUser();
  const currentUserRole = (currentUserObj?.role || currentUserObj?.userType || '').toLowerCase();
  const canSeePhone = currentUserRole === 'company' || 
                       currentUserRole === 'franchise' || 
                       currentUserRole === 'manager' || 
                       currentUserRole === 'admin' || 
                       currentUserRole === 'superadmin' || 
                       currentUserRole.includes('manager');

  // Get current user name - check all possible name fields
  // If company logged in, show company name, if employee show their name
  const getCurrentUserName = () => {
    const currentUser = getCurrentUser();
    console.log('👤 Current user object:', currentUser);
    console.log('👤 All localStorage keys:', Object.keys(localStorage));
    console.log('👤 userInfo:', localStorage.getItem('userInfo'));
    console.log('👤 userData:', localStorage.getItem('userData'));
    
    if (!currentUser) return 'Unknown';
    
    // Check if user is company - show company name
    const userRole = (currentUser.role || currentUser.userType || '').toLowerCase();
    console.log('👤 User role:', userRole);
    
    const isCompany = userRole === 'company' || userRole === 'franchise';
    
    if (isCompany) {
      // For company, show businessName or name
      const companyName = currentUser.businessName || currentUser.name;
      console.log('📝 Company user, name:', companyName);
      if (companyName && companyName !== 'Company') {
        return companyName;
      }
      // If no name, use email prefix
      if (currentUser.email) {
        const emailName = currentUser.email.split('@')[0];
        return emailName.charAt(0).toUpperCase() + emailName.slice(1) + ' (Company)';
      }
      return 'Company';
    }
    
    // For employees, show their name
    const name = currentUser.name || currentUser.teamMemberName || 
                 currentUser.firstName || currentUser.fullName || currentUser.displayName;
    console.log('📝 Employee user, name:', name);
    
    if (name && name !== 'Employee') {
      return name;
    }
    
    // Fallback to email prefix
    if (currentUser.email) {
      const emailName = currentUser.email.split('@')[0];
      return emailName.charAt(0).toUpperCase() + emailName.slice(1);
    }
    
    return 'Employee';
  };

  // Get current user email
  const getCurrentUserEmail = () => {
    const currentUser = getCurrentUser();
    if (!currentUser) return '';
    return currentUser.email || currentUser.businessEmail || currentUser.teamMemberEmail || '';
  };

  useEffect(() => { fetchLead(); fetchLeadStatuses(); }, [id]);

  const fetchLead = async () => {
    try {
      setLoading(true);
      const response = await leadsApi.get(`/leads/${id}`);
      setLead(response.data);
    } catch (err) {
      setError('Failed to load lead details');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeadStatuses = async () => {
    try {
      const response = await leadsApi.get('/lead-statuses');
      if (response.data.success) setLeadStatuses(response.data.statuses);
    } catch (err) { console.error('Error:', err); }
  };

  const handleStatusChange = async (remark) => {
    try {
      await leadsApi.put(`/leads/${id}`, { status: statusModal.newStatus, statusRemark: remark });
      fetchLead();
      setStatusModal({ isOpen: false, viewHistoryOnly: false, newStatus: '' });
    } catch (err) { console.error('Error:', err); }
  };

  const handleEditLead = async (leadData) => {
    try {
      const result = await LeadsService.updateLead(id, leadData);
      if (result.success) {
        fetchLead();
        setShowEditModal(false);
      } else {
        alert(`Failed to update lead: ${result.error}`);
      }
    } catch (err) {
      console.error('Error updating lead:', err);
      alert('Failed to update lead');
    }
  };

  const toggleSection = (s) => setExpandedSections(prev => ({ ...prev, [s]: !prev[s] }));

  const openRescheduleModal = (followUp, index) => {
    const currentDate = followUp.nextFollowUpDate || followUp.scheduledDate || followUp.date;
    setRescheduleData({
      newDate: currentDate ? new Date(currentDate).toISOString().slice(0, 16) : '',
      reason: '',
      reasonType: '',
      notes: ''
    });
    setRescheduleModal({ isOpen: true, followUp, followUpIndex: index });
  };

  const handleReschedule = async () => {
    if (!rescheduleData.newDate) {
      alert('Please select a new date');
      return;
    }
    
    setRescheduleLoading(true);
    try {
      const followUp = rescheduleModal.followUp;
      const followUpId = followUp._id;
      const previousDate = followUp.nextFollowUpDate || followUp.scheduledDate || followUp.date;
      
      const userName = getCurrentUserName();
      const userEmail = getCurrentUserEmail();
      console.log('📝 Rescheduling by user:', userName, 'email:', userEmail);
      
      // Build reschedule history entry with user info
      const newHistoryEntry = {
        previousDate: previousDate,
        newDate: new Date(rescheduleData.newDate).toISOString(),
        reason: rescheduleData.reason || 'Not specified',
        notes: rescheduleData.notes || '',
        rescheduledAt: new Date().toISOString(),
        rescheduledBy: userName,
        rescheduledByEmail: userEmail
      };
      
      console.log('📝 New history entry:', newHistoryEntry);
      
      // Get existing history or create new array
      const existingHistory = followUp.rescheduleHistory || [];
      const updatedHistory = [...existingHistory, newHistoryEntry];
      
      await leadsApi.put(`/follow-up/update/${id}/${followUpId}`, {
        scheduledDate: new Date(rescheduleData.newDate).toISOString(),
        nextFollowUpDate: new Date(rescheduleData.newDate).toISOString(),
        rescheduled: true,
        rescheduledAt: new Date().toISOString(),
        rescheduledFrom: previousDate,
        rescheduleHistory: updatedHistory
      });
      
      fetchLead();
      setRescheduleModal({ isOpen: false, followUp: null, followUpIndex: -1 });
      setRescheduleData({ newDate: '', reason: '', reasonType: '', notes: '' });
    } catch (err) {
      console.error('Error rescheduling:', err);
      alert('Failed to reschedule follow-up');
    } finally {
      setRescheduleLoading(false);
    }
  };

  const handleMarkComplete = async (followUp) => {
    try {
      await leadsApi.put(`/follow-up/update/${id}/${followUp._id}`, {
        completed: true,
        completedAt: new Date().toISOString(),
        status: 'completed',
        completedBy: getCurrentUserName(),
        completedByEmail: getCurrentUserEmail()
      });
      fetchLead();
    } catch (err) {
      console.error('Error marking complete:', err);
    }
  };

  const handleDeleteFollowUp = async (followUp) => {
    if (!window.confirm('Are you sure you want to delete this follow-up? This action cannot be undone.')) {
      return;
    }
    try {
      await leadsApi.delete(`/follow-up/delete/${id}/${followUp._id}`);
      fetchLead();
    } catch (err) {
      console.error('Error deleting follow-up:', err);
      alert('Failed to delete follow-up');
    }
  };

  const getStatusColor = (status) => {
    const c = {
      'new': { bg: 'bg-blue-500', light: 'bg-blue-100', text: 'text-blue-800' },
      'contacted': { bg: 'bg-yellow-500', light: 'bg-yellow-100', text: 'text-yellow-800' },
      'qualified': { bg: 'bg-purple-500', light: 'bg-purple-100', text: 'text-purple-800' },
      'quotated': { bg: 'bg-orange-500', light: 'bg-orange-100', text: 'text-orange-800' },
      'won': { bg: 'bg-green-500', light: 'bg-green-100', text: 'text-green-800' },
      'lost': { bg: 'bg-red-500', light: 'bg-red-100', text: 'text-red-800' }
    };
    return c[status?.toLowerCase()] || c['new'];
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A';
  const formatShortDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'N/A';
  const getFollowUpIcon = (t) => ({ 'call': '📞', 'email': '📧', 'meeting': '🤝', 'demo': '💻', 'proposal': '📄', 'message': '💬', 'assignment': '👤' }[t] || '📌');

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
    </div>
  );

  if (error || !lead) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center bg-white p-8 rounded-xl shadow">
        <p className="text-red-600 mb-4">{error || 'Lead not found'}</p>
        <button onClick={() => navigate('/lead-mng')} className="px-4 py-2 bg-blue-600 text-white rounded-lg">Back</button>
      </div>
    </div>
  );

  const sc = getStatusColor(lead.status);

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 md:py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex items-start gap-3">
            <button onClick={() => navigate('/lead-mng')} className="p-2 hover:bg-gray-100 rounded-lg flex-shrink-0 mt-1">
              <ArrowLeftIcon className="h-5 w-5 text-gray-600" />
            </button>
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="text-lg md:text-2xl font-bold text-gray-900 leading-tight">{lead.name}</h1>
                <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${sc.light} ${sc.text}`}>{lead.status || 'new'}</span>
              </div>
              {(lead.customFields?.title || lead.title) && (
                <p className="text-blue-600 text-sm font-medium">🎯 {lead.customFields?.title || lead.title}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-1.5 md:gap-2 pl-0 md:pl-0 mt-2 md:mt-0 w-full md:w-auto">
            <button
              onClick={() => setScheduleFollowUpModal(true)}
              className="col-span-1 px-1.5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex flex-col items-center justify-center gap-1 text-[9px] md:text-sm font-medium text-center leading-tight shadow-sm"
            >
              <CalendarIcon className="h-4 w-4 md:h-5 md:w-5 flex-shrink-0" />
              <span>Change Status<br className="md:hidden"/>& Follow-up</span>
            </button>
            <button onClick={() => setStatusModal({ isOpen: true, viewHistoryOnly: true, newStatus: lead.status })} className="col-span-1 px-1.5 py-2 border rounded-lg hover:bg-gray-50 flex flex-col items-center justify-center gap-1 text-[9px] md:text-sm font-medium text-center leading-tight shadow-sm bg-white">
              <ClockIcon className="h-4 w-4 md:h-5 md:w-5 text-gray-600" /> 
              <span>History</span>
            </button>
            <button onClick={() => setShowEditModal(true)} className="col-span-1 px-1.5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex flex-col items-center justify-center gap-1 text-[9px] md:text-sm font-medium text-center leading-tight shadow-sm">
              <PencilIcon className="h-4 w-4 md:h-5 md:w-5" /> 
              <span>Edit</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-2 md:px-4 py-3 md:py-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 md:gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-3 md:space-y-6">
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
              <div className="bg-white rounded-lg md:rounded-xl p-2 md:p-3 shadow-sm border text-center flex flex-col justify-center min-h-[50px]">
                <p className="text-[10px] md:text-xs text-gray-500 font-semibold uppercase tracking-wider mb-0.5">Status</p>
                <p className={`text-xs md:text-sm font-bold truncate leading-none ${sc.text}`}>{lead.status || 'New'}</p>
              </div>
              <div className="bg-white rounded-lg md:rounded-xl p-2 md:p-3 shadow-sm border text-center flex flex-col justify-center min-h-[50px]">
                <p className="text-[10px] md:text-xs text-gray-500 font-semibold uppercase tracking-wider mb-0.5">Follow-ups</p>
                <p className="text-sm md:text-base font-extrabold text-blue-600 leading-none">{lead.followUps?.length || 0}</p>
              </div>
              <div className="bg-white rounded-lg md:rounded-xl p-2 md:p-3 shadow-sm border text-center flex flex-col justify-center min-h-[50px]">
                <p className="text-[10px] md:text-xs text-gray-500 font-semibold uppercase tracking-wider mb-0.5">Changes</p>
                <p className="text-sm md:text-base font-extrabold text-blue-600 leading-none">{lead.statusHistory?.length || 0}</p>
              </div>
              <div className="bg-white rounded-lg md:rounded-xl p-2 md:p-3 shadow-sm border text-center flex flex-col justify-center min-h-[50px]">
                <p className="text-[10px] md:text-xs text-gray-500 font-semibold uppercase tracking-wider mb-0.5">Assigned</p>
                <p className="text-sm md:text-base font-extrabold text-blue-600 leading-none">{lead.assignedEmployees?.length || 0}</p>
              </div>
            </div>

            {/* Contact */}
            <div className="bg-white rounded-lg md:rounded-xl shadow-sm border">
              <button onClick={() => toggleSection('contact')} className="w-full px-4 md:px-6 py-3 md:py-4 flex justify-between items-center bg-blue-50 border-b rounded-t-lg md:rounded-t-xl">
                <h2 className="font-semibold flex items-center gap-2 text-sm md:text-base"><UserIcon className="h-4 w-4 md:h-5 md:w-5 text-blue-600" /> Contact Info</h2>
                {expandedSections.contact ? <ChevronUpIcon className="h-4 w-4 md:h-5 md:w-5" /> : <ChevronDownIcon className="h-4 w-4 md:h-5 md:w-5" />}
              </button>
              {expandedSections.contact && (
                <div className="p-3 md:p-6 grid grid-cols-1 sm:grid-cols-2 gap-2 md:gap-4">
                  <a href={`mailto:${lead.email}`} className="flex items-center gap-2 md:gap-3 p-3 bg-gray-50 rounded-lg md:rounded-xl hover:bg-blue-50 min-w-0">
                    <EnvelopeIcon className="h-5 w-5 md:h-6 md:w-6 text-blue-600 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-[10px] md:text-xs text-gray-500 font-medium">Email</p>
                      <p className="text-blue-600 font-medium text-xs md:text-sm truncate">{lead.email}</p>
                    </div>
                  </a>
                  {lead.phone && (
                    canSeePhone ? (
                      <a href={`tel:${lead.phone}`} className="flex items-center gap-2 md:gap-3 p-3 bg-gray-50 rounded-lg md:rounded-xl hover:bg-green-50 min-w-0">
                        <PhoneIcon className="h-5 w-5 md:h-6 md:w-6 text-green-600 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] md:text-xs text-gray-500 font-medium">Phone</p>
                          <p className="text-green-600 font-medium text-xs md:text-sm truncate">{lead.phone}</p>
                        </div>
                      </a>
                    ) : (
                      <div className="flex items-center gap-2 md:gap-3 p-3 bg-gray-50 rounded-lg md:rounded-xl min-w-0 cursor-not-allowed" title="Only manager and company can view full contact number">
                        <PhoneIcon className="h-5 w-5 md:h-6 md:w-6 text-gray-400 shrink-0" />
                        <div className="min-w-0 flex-1">
                          <p className="text-[10px] md:text-xs text-gray-500 font-medium">Phone</p>
                          <p className="text-gray-400 font-medium text-xs md:text-sm truncate">
                            {lead.phone.toString().trim().length > 4 
                              ? '*'.repeat(lead.phone.toString().trim().length - 4) + lead.phone.toString().trim().slice(-4)
                              : '******'}
                          </p>
                        </div>
                      </div>
                    )
                  )}
                  {lead.company && (
                    <div className="flex items-center gap-2 md:gap-3 p-3 bg-gray-50 rounded-lg md:rounded-xl min-w-0">
                      <BuildingOfficeIcon className="h-5 w-5 md:h-6 md:w-6 text-purple-600 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] md:text-xs text-gray-500 font-medium">Company</p>
                        <p className="font-medium text-xs md:text-sm truncate">{lead.company}</p>
                      </div>
                    </div>
                  )}
                  {lead.source && (
                    <div className="flex items-center gap-2 md:gap-3 p-3 bg-gray-50 rounded-lg md:rounded-xl min-w-0">
                      <TagIcon className="h-5 w-5 md:h-6 md:w-6 text-orange-600 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="text-[10px] md:text-xs text-gray-500 font-medium">Source</p>
                        <p className="font-medium text-xs md:text-sm capitalize truncate">{lead.source}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Follow-ups */}
            <div className="bg-white rounded-lg md:rounded-xl shadow-sm border">
              <button onClick={() => toggleSection('followups')} className="w-full px-4 md:px-6 py-3 md:py-4 flex justify-between items-center bg-blue-50 border-b rounded-t-lg md:rounded-t-xl">
                <h2 className="font-semibold flex items-center gap-2 text-sm md:text-base">
                  <ChatBubbleLeftRightIcon className="h-4 w-4 md:h-5 md:w-5 text-blue-600" /> Follow-ups
                  <span className="ml-1 md:ml-2 px-1.5 md:px-2 py-0.5 bg-blue-100 text-blue-700 text-[10px] md:text-xs rounded-full">{lead.followUps?.length || 0}</span>
                </h2>
                {expandedSections.followups ? <ChevronUpIcon className="h-4 w-4 md:h-5 md:w-5" /> : <ChevronDownIcon className="h-4 w-4 md:h-5 md:w-5" />}
              </button>
              {expandedSections.followups && (
                <div className="p-6">
                  {lead.followUps?.length > 0 ? (
                    <div className="space-y-4">
                      {lead.followUps.slice().reverse().map((f, idx) => {
                        const actualIndex = lead.followUps.length - 1 - idx;
                        return (
                        <div key={idx} className={`p-4 rounded-xl border-2 ${f.completed ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                          <div className="flex justify-between items-start mb-2">
                            <div className="flex items-center gap-2">
                              <span className="text-xl">{getFollowUpIcon(f.type)}</span>
                              <span className="font-semibold capitalize">{f.type}</span>
                              {f.rescheduled && <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">Rescheduled</span>}
                            </div>
                            <div className="flex items-center gap-2">
                              <span className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold ${f.completed ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                {f.completed ? <CheckCircleIcon className="h-4 w-4" /> : <ExclamationCircleIcon className="h-4 w-4" />}
                                {f.completed ? 'Done' : 'Pending'}
                              </span>
                            </div>
                          </div>
                          <p className="font-medium text-gray-900">{f.summary}</p>
                          {f.notes && <p className="text-sm text-gray-600 mt-2 bg-white/50 p-2 rounded">{f.notes}</p>}
                          
                          {/* Info Row */}
                          <div className="flex items-center flex-wrap gap-3 mt-3 text-xs text-gray-500">
                            <span className="flex items-center gap-1">
                              <CalendarIcon className="h-3.5 w-3.5" /> Created: {formatDate(f.date)}
                            </span>
                            {f.addedByName && (
                              <span className="flex items-center gap-1 bg-gray-100 px-2 py-0.5 rounded">
                                <UserIcon className="h-3 w-3" /> Added by: <span className="font-medium text-gray-700">{f.addedByName}</span>
                              </span>
                            )}
                            {(f.nextFollowUpDate || f.scheduledDate) && (
                              <span className="text-blue-600 font-medium flex items-center gap-1">
                                📅 Scheduled: {formatDate(f.nextFollowUpDate || f.scheduledDate)}
                              </span>
                            )}
                          </div>
                          
                          {/* Completed Info */}
                          {f.completed && (
                            <div className="mt-2 p-2 bg-green-100 rounded-lg">
                              <p className="text-xs text-green-800 flex items-center gap-2">
                                <CheckCircleIcon className="h-4 w-4" />
                                <span>Completed on {formatDate(f.completedAt)}</span>
                                {f.completedBy && <span className="font-medium">by {f.completedBy}</span>}
                              </p>
                            </div>
                          )}
                          
                          {/* Action Buttons */}
                          {!f.completed && (
                            <div className="flex flex-wrap items-center gap-2 mt-3">
                              <button
                                onClick={() => openRescheduleModal(f, actualIndex)}
                                className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-200 flex items-center gap-1"
                              >
                                <ArrowPathIcon className="h-3.5 w-3.5" /> Reschedule
                              </button>
                              <button
                                onClick={() => handleMarkComplete(f)}
                                className="px-3 py-1.5 bg-green-100 text-green-700 rounded-lg text-xs font-medium hover:bg-green-200 flex items-center gap-1"
                              >
                                <CheckCircleIcon className="h-3.5 w-3.5" /> Complete
                              </button>
                              <button
                                onClick={() => handleDeleteFollowUp(f)}
                                className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200 flex items-center gap-1"
                              >
                                <TrashIcon className="h-3.5 w-3.5" /> Delete
                              </button>
                            </div>
                          )}
                          
                          {/* Delete button for completed follow-ups */}
                          {f.completed && (
                            <div className="flex items-center gap-2 mt-3">
                              <button
                                onClick={() => handleDeleteFollowUp(f)}
                                className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-xs font-medium hover:bg-red-200 flex items-center gap-1"
                              >
                                <TrashIcon className="h-3.5 w-3.5" /> Delete
                              </button>
                            </div>
                          )}
                          
                          {/* Reschedule History - Collapsible */}
                          {f.rescheduleHistory?.length > 0 && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                              <button
                                onClick={() => setExpandedHistories(prev => ({ ...prev, [f._id]: !prev[f._id] }))}
                                className="flex items-center gap-2 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
                              >
                                <ArrowPathIcon className="h-4 w-4" />
                                <span>{expandedHistories[f._id] ? 'Hide' : 'Show'} Reschedule History ({f.rescheduleHistory.length})</span>
                                {expandedHistories[f._id] ? <ChevronUpIcon className="h-4 w-4" /> : <ChevronDownIcon className="h-4 w-4" />}
                              </button>
                              
                              {expandedHistories[f._id] && (
                                <div className="mt-3 space-y-2 pl-2 border-l-2 border-blue-200">
                                  {f.rescheduleHistory.map((rh, rhIdx) => (
                                    <div key={rhIdx} className="relative pl-4">
                                      <div className="absolute -left-[5px] top-2 w-2 h-2 bg-blue-500 rounded-full"></div>
                                      <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                                        <div className="flex items-center gap-2 text-sm">
                                          <span className="text-gray-600">{formatShortDate(rh.previousDate)}</span>
                                          <span className="text-blue-600 font-bold">→</span>
                                          <span className="text-blue-700 font-medium">{formatShortDate(rh.newDate)}</span>
                                        </div>
                                        {rh.reason && (
                                          <p className="text-xs text-gray-600 mt-1">
                                            <span className="font-medium">Reason:</span> {rh.reason}
                                          </p>
                                        )}
                                        {rh.notes && (
                                          <p className="text-xs text-gray-500 mt-1 italic">"{rh.notes}"</p>
                                        )}
                                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                                          <span>{formatDate(rh.rescheduledAt)}</span>
                                          {rh.rescheduledBy && (
                                            <span className="bg-blue-100 px-2 py-0.5 rounded text-blue-700">
                                              by {rh.rescheduledBy}
                                            </span>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )})}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <ChatBubbleLeftRightIcon className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                      <p>No follow-ups yet</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {lead.notes && (
              <div className="bg-white rounded-xl shadow-sm border p-6">
                <h2 className="font-semibold mb-3">📝 Notes</h2>
                <p className="text-gray-700 bg-gray-50 p-4 rounded-xl whitespace-pre-wrap">{lead.notes}</p>
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">


            {/* Assigned */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="h-2 bg-blue-500"></div>
              <div className="p-6">
                <h3 className="text-sm text-gray-500 uppercase mb-3 flex items-center gap-2"><UserGroupIcon className="h-4 w-4" /> Assigned To</h3>
                {lead.assignedEmployees?.length > 0 ? (
                  <div className="space-y-2">
                    {lead.assignedEmployees.map((emp, idx) => (
                      <div key={idx} className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl">
                        <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">{(emp.name || 'E')[0]}</div>
                        <div>
                          <p className="font-semibold">{emp.name}</p>
                          {emp.email && <p className="text-xs text-gray-500">{emp.email}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : lead.assignedEmployeeName ? (
                  <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-xl">
                    <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">{lead.assignedEmployeeName[0]}</div>
                    <p className="font-semibold">{lead.assignedEmployeeName}</p>
                  </div>
                ) : (
                  <div className="text-center py-6 bg-gray-50 rounded-xl text-gray-500 text-sm">Not assigned</div>
                )}
              </div>
            </div>

            {/* Value */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="h-2 bg-blue-500"></div>
              <div className="p-6">
                <h3 className="text-sm text-gray-500 uppercase mb-3 flex items-center gap-2"><BanknotesIcon className="h-4 w-4" /> Value</h3>
                {lead.value > 0 && <div className="flex justify-between p-3 bg-blue-50 rounded-xl mb-2"><span>Value</span><span className="font-bold text-blue-600">₹{lead.value.toLocaleString()}</span></div>}
                {lead.budget && <div className="flex justify-between p-3 bg-blue-50 rounded-xl"><span>Budget</span><span className="font-bold text-blue-600">{lead.budget}</span></div>}
                {!lead.value && !lead.budget && <div className="text-center py-6 bg-gray-50 rounded-xl text-gray-500 text-sm">No value set</div>}
              </div>
            </div>

            {/* Timeline */}
            <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
              <div className="h-2 bg-blue-500"></div>
              <div className="p-6">
                <h3 className="text-sm text-gray-500 uppercase mb-3 flex items-center gap-2"><CalendarIcon className="h-4 w-4" /> Timeline</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between p-3 bg-blue-50 rounded-xl"><span>Created</span><span className="font-medium">{formatShortDate(lead.createdAt)}</span></div>
                  <div className="flex justify-between p-3 bg-blue-50 rounded-xl"><span>Last Contact</span><span className="font-medium">{formatShortDate(lead.lastContact)}</span></div>
                  {lead.assignedAt && <div className="flex justify-between p-3 bg-blue-50 rounded-xl"><span>Assigned</span><span className="font-medium">{formatShortDate(lead.assignedAt)}</span></div>}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <StatusChangeModal
        isOpen={statusModal.isOpen}
        onClose={() => setStatusModal({ isOpen: false, viewHistoryOnly: false, newStatus: '' })}
        lead={lead}
        newStatus={statusModal.newStatus}
        statusHistory={lead?.statusHistory || []}
        viewHistoryOnly={statusModal.viewHistoryOnly}
        onConfirm={handleStatusChange}
      />

      <FollowUpModal
        isOpen={scheduleFollowUpModal}
        onClose={() => setScheduleFollowUpModal(false)}
        lead={lead}
        onFollowUpAdded={fetchLead}
        currentUser={getCurrentUser()}
      />

      {/* Edit Lead Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-stretch sm:items-center justify-center p-0 sm:p-4 z-[9999]">
          <div className="bg-white rounded-none sm:rounded-xl shadow-2xl max-w-2xl w-full h-full sm:h-auto max-h-full sm:max-h-[90vh] flex flex-col overflow-hidden">
            <LeadForm
              lead={lead}
              onSubmit={handleEditLead}
              onCancel={() => setShowEditModal(false)}
            />
          </div>
        </div>
      )}

      {/* Reschedule Modal */}
      {rescheduleModal.isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4">
            <div className="px-6 py-4 border-b flex items-center justify-between bg-gradient-to-r from-blue-600 to-indigo-600 rounded-t-xl">
              <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                <ArrowPathIcon className="h-5 w-5" /> Reschedule Follow-up
              </h3>
              <button onClick={() => setRescheduleModal({ isOpen: false, followUp: null, followUpIndex: -1 })} className="text-white/80 hover:text-white">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              {rescheduleModal.followUp && (
                <div className="bg-gray-50 p-4 rounded-xl">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xl">{getFollowUpIcon(rescheduleModal.followUp.type)}</span>
                    <span className="font-semibold capitalize">{rescheduleModal.followUp.type}</span>
                  </div>
                  <p className="text-sm text-gray-700">{rescheduleModal.followUp.summary}</p>
                  {(rescheduleModal.followUp.nextFollowUpDate || rescheduleModal.followUp.scheduledDate) && (
                    <p className="text-xs text-gray-500 mt-2">
                      Current: {formatDate(rescheduleModal.followUp.nextFollowUpDate || rescheduleModal.followUp.scheduledDate)}
                    </p>
                  )}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  New Date & Time <span className="text-red-500">*</span>
                </label>
                <input
                  type="datetime-local"
                  value={rescheduleData.newDate}
                  onChange={(e) => setRescheduleData(prev => ({ ...prev, newDate: e.target.value }))}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  min={new Date().toISOString().slice(0, 16)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Reason for Reschedule</label>
                <div className="space-y-2">
                  <select
                    value={rescheduleData.reasonType || ''}
                    onChange={(e) => {
                      const val = e.target.value;
                      if (val === 'custom') {
                        setRescheduleData(prev => ({ ...prev, reasonType: 'custom', reason: '' }));
                      } else {
                        setRescheduleData(prev => ({ ...prev, reasonType: val, reason: val }));
                      }
                    }}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Select reason...</option>
                    <option value="Customer requested">Customer requested</option>
                    <option value="Not available">Not available</option>
                    <option value="Schedule conflict">Schedule conflict</option>
                    <option value="Need more preparation">Need more preparation</option>
                    <option value="Weather/Travel issues">Weather/Travel issues</option>
                    <option value="Customer not responding">Customer not responding</option>
                    <option value="Internal meeting">Internal meeting</option>
                    <option value="custom">✏️ Enter custom reason...</option>
                  </select>
                  
                  {rescheduleData.reasonType === 'custom' && (
                    <input
                      type="text"
                      value={rescheduleData.reason}
                      onChange={(e) => setRescheduleData(prev => ({ ...prev, reason: e.target.value }))}
                      placeholder="Enter your reason..."
                      className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Additional Notes</label>
                <textarea
                  value={rescheduleData.notes}
                  onChange={(e) => setRescheduleData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500"
                  placeholder="Any additional notes..."
                />
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t rounded-b-xl flex justify-end gap-3">
              <button
                onClick={() => setRescheduleModal({ isOpen: false, followUp: null, followUpIndex: -1 })}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                onClick={handleReschedule}
                disabled={rescheduleLoading || !rescheduleData.newDate}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                {rescheduleLoading && <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>}
                {rescheduleLoading ? 'Saving...' : 'Reschedule'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadProfile;
